import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  Banknote,
  BellRing,
  CalendarCheck2,
  CheckCircle2,
  ClipboardList,
  CreditCard,
  MapPin,
  Search,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { StoreButtons } from "@/components/StoreButtons";
import { Seo } from "@/components/Seo";
import { HeroScene } from "@/components/illustrations/HeroScene";
import { MiniCar } from "@/components/illustrations/MiniCar";

const HEADLINE = ["Find.", "Book.", "Learn."];

const MARQUEE = [
  "Discover instructors",
  "Book lessons",
  "Manage bookings",
  "Pay online with Stripe",
  "Cash where offered",
  "Automatic or manual",
];

const STEPS = [
  {
    step: "01",
    icon: Search,
    title: "Discover instructors",
    desc: "Browse driving instructors near you and compare lesson options, transmission types and availability in one place.",
  },
  {
    step: "02",
    icon: CalendarCheck2,
    title: "Book lessons",
    desc: "Pick a time that suits you and book your driving lesson in a few taps — no phone calls, no back-and-forth.",
  },
  {
    step: "03",
    icon: ClipboardList,
    title: "Manage bookings",
    desc: "Keep every upcoming and past lesson organised, stay on top of your schedule and focus on learning to drive.",
  },
];

const BENTO = [
  {
    id: "discover",
    icon: MapPin,
    title: "Instructors near you",
    desc: "DELSHA helps learners discover driving instructors in their area, so finding the right match takes minutes — not weeks of ringing around.",
    span: "md:col-span-4",
  },
  {
    id: "booking",
    icon: CalendarCheck2,
    title: "Booking in a few taps",
    desc: "Choose a slot, confirm, and your lesson is booked.",
    span: "md:col-span-2",
  },
  {
    id: "manage",
    icon: ClipboardList,
    title: "Every booking, organised",
    desc: "See upcoming lessons, review past sessions and stay in control of your learning schedule.",
    span: "md:col-span-2",
  },
  {
    id: "payments",
    icon: CreditCard,
    title: "Payments your way",
    desc: "Pay online through Stripe where supported, or arrange cash directly with instructors who offer that option.",
    span: "md:col-span-4",
  },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const sceneY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <div data-testid="home-page">
      <Seo
        title="DELSHA — Find. Book. Learn. | Driving Lesson Booking App"
        description="DELSHA connects learner drivers with driving instructors. Discover instructors near you, book lessons in a few taps and manage every booking in one app."
      />
      {/* Hero */}
      <section ref={heroRef} className="relative overflow-hidden pt-28 md:pt-36">
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute -right-40 top-10 hidden h-[560px] w-[560px] text-blue-100 lg:block"
          viewBox="0 0 200 200"
          fill="none"
        >
          <path
            d="M10 170 C 60 170, 60 90, 100 90 S 140 10, 190 10"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeDasharray="6 8"
          />
        </svg>

        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-16 sm:px-6 md:pb-24 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-blue-600"
              data-testid="hero-eyebrow"
            >
              The driving lesson booking app
            </motion.p>

            <h1
              data-testid="hero-headline"
              className="mt-6 font-display font-extrabold tracking-tight text-slate-900"
            >
              {HEADLINE.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block text-5xl leading-[1.02] sm:text-6xl lg:text-7xl"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{
                      duration: 0.9,
                      delay: 0.15 + i * 0.14,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                  >
                    {line.slice(0, -1)}
                    <span className="text-blue-600">.</span>
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 max-w-lg text-base leading-relaxed text-slate-600 md:text-lg"
              data-testid="hero-subtitle"
            >
              DELSHA connects learner drivers with driving instructors — discover
              instructors near you, book lessons in a few taps and manage every
              booking in one place.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
              className="mt-9"
            >
              <StoreButtons />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <motion.div style={{ y: sceneY }}>
              <HeroScene />
            </motion.div>

            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-4 top-8 flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/90 px-4 py-3 shadow-lg backdrop-blur-md sm:-left-8"
              data-testid="hero-chip-booked"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                <CheckCircle2 className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-sm font-semibold text-slate-900">
                  Lesson booked
                </span>
                <span className="block text-xs text-slate-500">
                  Confirmation in-app
                </span>
              </span>
            </motion.div>

            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -bottom-5 right-4 flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/90 px-4 py-3 shadow-lg backdrop-blur-md sm:right-8"
              data-testid="hero-chip-pickup"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                <MapPin className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-sm font-semibold text-slate-900">
                  Pick-up point set
                </span>
                <span className="block text-xs text-slate-500">
                  Managed in the app
                </span>
              </span>
            </motion.div>
          </motion.div>
        </div>

        {/* Marquee */}
        <div
          className="overflow-hidden border-y border-slate-200/80 bg-slate-50 py-5"
          data-testid="editorial-marquee"
        >
          <div className="flex w-max animate-marquee">
            {[0, 1].map((copy) => (
              <div key={copy} className="flex items-center" aria-hidden={copy === 1}>
                {MARQUEE.map((item) => (
                  <span
                    key={`${copy}-${item}`}
                    className="flex items-center gap-8 whitespace-nowrap px-8 font-display text-sm font-semibold uppercase tracking-[0.25em] text-slate-500"
                  >
                    {item}
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="scroll-mt-24 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-blue-600">
              How DELSHA works
            </p>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              On the road in three simple steps
            </h2>
          </Reveal>

          <div className="relative mt-14 grid gap-10 md:grid-cols-3 md:gap-8">
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute -top-6 left-0 hidden h-10 w-full text-blue-200 md:block"
              viewBox="0 0 900 40"
              fill="none"
              preserveAspectRatio="none"
            >
              <motion.path
                d="M0 30 C 150 5, 300 40, 450 20 S 750 35, 900 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="7 9"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease: "easeInOut" }}
              />
            </svg>
            {STEPS.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.12}>
                <div
                  data-testid={`how-it-works-step-${i + 1}`}
                  className="group relative h-full rounded-2xl border border-slate-200/80 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-md"
                >
                  <div className="flex items-center justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition-transform duration-300 group-hover:scale-110">
                      <s.icon className="h-6 w-6" />
                    </span>
                    <span className="font-display text-4xl font-extrabold text-blue-100 transition-colors duration-300 group-hover:text-blue-200">
                      {s.step}
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-lg font-semibold text-slate-900 sm:text-xl">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {s.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Bento */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-blue-600">
              Why DELSHA
            </p>
            <h2 className="mt-4 max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Everything a learner driver needs, in one app
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-6">
            {BENTO.map((card, i) => (
              <Reveal key={card.id} delay={i * 0.1} className={card.span}>
                <div
                  data-testid={`bento-card-${card.id}`}
                  className="group h-full rounded-2xl border border-slate-200/80 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-md sm:p-8"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm transition-transform duration-300 group-hover:scale-110">
                    <card.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-6 font-display text-lg font-semibold text-slate-900 sm:text-xl">
                    {card.title}
                  </h3>
                  <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600">
                    {card.desc}
                  </p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.2} className="md:col-span-6">
              <div
                data-testid="bento-card-reminders"
                className="group flex flex-col items-start gap-5 rounded-2xl border border-blue-100 bg-blue-50/80 p-7 sm:flex-row sm:items-center sm:p-8"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm transition-transform duration-300 group-hover:scale-110">
                  <BellRing className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-display text-lg font-semibold text-slate-900 sm:text-xl">
                    Built around your schedule
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    From your first lesson to test day, DELSHA keeps your
                    bookings, lesson details and payments together — so you can
                    focus on becoming a confident driver.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Payments */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-blue-600">
              Payments
            </p>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Clear, flexible ways to pay
            </h2>
          </Reveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <Reveal>
              <div
                data-testid="payments-stripe-card"
                className="h-full rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-md"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <CreditCard className="h-6 w-6" />
                </span>
                <h3 className="mt-6 font-display text-lg font-semibold text-slate-900 sm:text-xl">
                  Pay online with Stripe
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  Online payments can be made securely through Stripe where
                  supported, so you can pay for lessons in the app with your
                  card.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div
                data-testid="payments-cash-card"
                className="h-full rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-md"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <Banknote className="h-6 w-6" />
                </span>
                <h3 className="mt-6 font-display text-lg font-semibold text-slate-900 sm:text-xl">
                  Pay with cash
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  Cash payments may be arranged directly with instructors who
                  offer that option. Not every instructor accepts cash — the
                  available payment options are shown before you book.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Download CTA */}
      <section id="download" className="scroll-mt-24 border-t border-slate-200/80 bg-slate-50 py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <Reveal>
            <MiniCar className="mx-auto h-24 w-auto" />
            <img
              src="/assets/delsha-wordmark.png"
              alt="DELSHA"
              className="mx-auto mt-6 h-10 w-auto"
            />
            <h2 className="mt-6 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Ready to start your driving journey?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-600">
              DELSHA is coming to the App Store and Google Play. The official
              download links will be published here at launch.
            </p>
            <div className="mt-9 flex justify-center">
              <StoreButtons centered />
            </div>
            <Link
              to="/about"
              data-testid="cta-about-link"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-blue-600 transition-colors hover:text-blue-700"
            >
              Learn more about DELSHA
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
